import { supabase } from './supabase';

export interface FeedbackFormField {
  id: string;
  business_id: string;
  field_key: string;
  field_label: string;
  field_type: 'rating' | 'text';
  is_required: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FeedbackFormConfig {
  business_id: string;
  fields: FeedbackFormField[];
  version: number;
}

export interface CreateFieldRequest {
  field_key: string;
  field_label: string;
  field_type: 'rating' | 'text';
  is_required: boolean;
  display_order: number;
}

export interface UpdateFieldRequest {
  field_label?: string;
  field_type?: 'rating' | 'text';
  is_required?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface DynamicFeedbackResponse {
  [fieldKey: string]: string | number;
}

/**
 * Get feedback form configuration for a business
 */
export async function getFeedbackFormConfig(businessId: string): Promise<FeedbackFormField[]> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('feedback_form_fields')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching feedback form config:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching feedback form config:', error);
    return [];
  }
}

/**
 * Get all feedback form fields for a business (only active fields)
 */
export async function getAllFeedbackFormFields(businessId: string): Promise<FeedbackFormField[]> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('feedback_form_fields')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching feedback form fields:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching feedback form fields:', error);
    return [];
  }
}

/**
 * Create a new feedback form field
 */
export async function createFeedbackFormField(
  businessId: string, 
  field: CreateFieldRequest
): Promise<FeedbackFormField | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('feedback_form_fields')
      .insert({
        business_id: businessId,
        field_key: field.field_key,
        field_label: field.field_label,
        field_type: field.field_type,
        is_required: field.is_required,
        display_order: field.display_order,
        is_active: true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating feedback form field:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error creating feedback form field:', error);
    return null;
  }
}

/**
 * Update a feedback form field
 */
export async function updateFeedbackFormField(
  fieldId: string,
  updates: UpdateFieldRequest
): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    const { error } = await supabase
      .from('feedback_form_fields')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', fieldId);

    if (error) {
      console.error('Error updating feedback form field:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating feedback form field:', error);
    return false;
  }
}

/**
 * Delete a feedback form field (soft delete by setting is_active to false)
 */
export async function deleteFeedbackFormField(fieldId: string): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    const { error } = await supabase
      .from('feedback_form_fields')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', fieldId);

    if (error) {
      console.error('Error deleting feedback form field:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting feedback form field:', error);
    return false;
  }
}

/**
 * Reorder feedback form fields
 */
export async function reorderFeedbackFormFields(
  businessId: string,
  fieldOrders: { id: string; display_order: number }[]
): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    // Update all field orders in a transaction-like manner
    const updates = fieldOrders.map(({ id, display_order }) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }
      return supabase
        .from('feedback_form_fields')
        .update({ 
          display_order,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('business_id', businessId);
    });

    const results = await Promise.all(updates);
    
    // Check if any updates failed
    const hasErrors = results.some(result => result.error);
    if (hasErrors) {
      console.error('Error reordering feedback form fields');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error reordering feedback form fields:', error);
    return false;
  }
}

/**
 * Submit dynamic feedback using the new form system
 */
export async function submitDynamicFeedback(
  token: string,
  responses: DynamicFeedbackResponse
): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    const { data, error } = await supabase
      .rpc('submit_dynamic_feedback', {
        feedback_token: token,
        responses: responses
      });

    if (error) {
      console.error('Error submitting dynamic feedback:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error submitting dynamic feedback:', error);
    return false;
  }
}

/**
 * Validate dynamic feedback responses against form configuration
 */
export function validateFeedbackResponses(
  responses: DynamicFeedbackResponse,
  formConfig: FeedbackFormField[]
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const field of formConfig) {
    const value = responses[field.field_key];

    // Check required fields
    if (field.is_required && (value === undefined || value === null || value === '')) {
      errors.push(`${field.field_label} is required`);
      continue;
    }

    // Skip validation if field is not provided and not required
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Validate rating fields
    if (field.field_type === 'rating') {
      const numValue = Number(value);
      if (isNaN(numValue) || numValue < 1 || numValue > 5) {
        errors.push(`${field.field_label} must be a rating between 1 and 5`);
      }
    }

    // Validate text fields
    if (field.field_type === 'text') {
      if (typeof value !== 'string') {
        errors.push(`${field.field_label} must be text`);
      } else if (value.length > 1000) {
        errors.push(`${field.field_label} must be less than 1000 characters`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Generate a unique field key from field label
 */
export function generateFieldKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, '') // Remove leading/trailing underscores
    .substring(0, 50); // Limit to 50 characters
}

/**
 * Get the next display order for a new field
 */
export async function getNextDisplayOrder(businessId: string): Promise<number> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return 1;
  }

  try {
    const { data, error } = await supabase
      .from('feedback_form_fields')
      .select('display_order')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('display_order', { ascending: false })
      .limit(1);

    if (error || !data || data.length === 0) {
      return 1;
    }

    return data[0].display_order + 1;
  } catch (error) {
    console.error('Error getting next display order:', error);
    return 1;
  }
} 
 